/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  deleteUndefined,
  firstNonNullable,
  makeNonNullableArray,
  pickFromUnion,
  toNumber,
  toString,
} from '@liquorice/allsorts-craftcms-nextjs';
import { gql } from 'graphql-request';
import { ImageFragment, ImageSize_AllFragment } from './_generated/image.generated';

// fragment imageSize_thumb on AssetInterface {
//   size_thumb_url: url @transform(transform: "thumb")
//   size_thumb_height: height @transform(transform: "thumb")
//   size_thumb_width: width @transform(transform: "thumb")
// }

gql`
  fragment imageSize_original on AssetInterface {
    original_url: url
    original_height: width
    original_width: height
  }
  #
  fragment imageSize_bannerCrop on AssetInterface {
    bannerCrop_url: url @transform(transform: "bannerCrop")
    bannerCrop_width: width @transform(transform: "bannerCrop")
    bannerCrop_height: height @transform(transform: "bannerCrop")
  }
  fragment imageSize_fullFit on AssetInterface {
    fullFit_url: url @transform(transform: "fullFit")
    fullFit_width: width @transform(transform: "fullFit")
    fullFit_height: height @transform(transform: "fullFit")
  }
  fragment imageSize_landscapeLgCrop on AssetInterface {
    landscapeLgCrop_url: url @transform(transform: "landscapeLgCrop")
    landscapeLgCrop_width: width @transform(transform: "landscapeLgCrop")
    landscapeLgCrop_height: height @transform(transform: "landscapeLgCrop")
  }
  fragment imageSize_landscapeMdCrop on AssetInterface {
    landscapeMdCrop_url: url @transform(transform: "landscapeMdCrop")
    landscapeMdCrop_width: width @transform(transform: "landscapeMdCrop")
    landscapeMdCrop_height: height @transform(transform: "landscapeMdCrop")
  }
  fragment imageSize_landscapeSmCrop on AssetInterface {
    landscapeSmCrop_url: url @transform(transform: "landscapeSmCrop")
    landscapeSmCrop_width: width @transform(transform: "landscapeSmCrop")
    landscapeSmCrop_height: height @transform(transform: "landscapeSmCrop")
  }
  fragment imageSize_squareMdFit on AssetInterface {
    squareMdFit_url: url @transform(transform: "squareMdFit")
    squareMdFit_width: width @transform(transform: "squareMdFit")
    squareMdFit_height: height @transform(transform: "squareMdFit")
  }
  fragment imageSize_squareSmFit on AssetInterface {
    squareSmFit_url: url @transform(transform: "squareSmFit")
    squareSmFit_width: width @transform(transform: "squareSmFit")
    squareSmFit_height: height @transform(transform: "squareSmFit")
  }
  fragment imageSize_squareLgCrop on AssetInterface {
    squareLgCrop_url: url @transform(transform: "squareLgCrop")
    squareLgCrop_width: width @transform(transform: "squareLgCrop")
    squareLgCrop_height: height @transform(transform: "squareLgCrop")
  }
  fragment imageSize_squareMdCrop on AssetInterface {
    squareMdCrop_url: url @transform(transform: "squareMdCrop")
    squareMdCrop_width: width @transform(transform: "squareMdCrop")
    squareMdCrop_height: height @transform(transform: "squareMdCrop")
  }
  fragment imageSize_squareSmCrop on AssetInterface {
    squareSmCrop_url: url @transform(transform: "squareSmCrop")
    squareSmCrop_width: width @transform(transform: "squareSmCrop")
    squareSmCrop_height: height @transform(transform: "squareSmCrop")
  }

  fragment imageSize_all on AssetInterface {
    ...imageSize_original
    ...imageSize_bannerCrop
    ...imageSize_fullFit
    ...imageSize_landscapeLgCrop
    ...imageSize_landscapeMdCrop
    ...imageSize_landscapeSmCrop
    ...imageSize_squareMdFit
    ...imageSize_squareSmFit
    ...imageSize_squareLgCrop
    ...imageSize_squareMdCrop
    ...imageSize_squareSmCrop
  }

  fragment imageSize_common on AssetInterface {
    ...imageSize_original
    ...imageSize_bannerCrop
    ...imageSize_fullFit
    ...imageSize_landscapeLgCrop
    ...imageSize_landscapeMdCrop
    ...imageSize_landscapeSmCrop
    # ...imageSize_squareMdFit
    # ...imageSize_squareSmFit
    ...imageSize_squareLgCrop
    ...imageSize_squareMdCrop
    ...imageSize_squareSmCrop
  }

  fragment imageBase on AssetInterface {
    mimeType
    id
    alt
    title
    url @transform(transform: "fullFit")
    height @transform(transform: "fullFit")
    width @transform(transform: "fullFit")
  }

  fragment image on AssetInterface {
    ...imageBase
    ...imageSize_common
  }
`;

export type ImageTransform = {
  url: string;
  height: number;
  width: number;
};

export type ImageTransformType = Extract<
  keyof ImageSize_AllFragment,
  `${string}_url`
> extends `${infer Size}_url`
  ? Size
  : never;

export type ImageTransforms = { [P in ImageTransformType]?: ImageTransform };

export const imageTransformKeys: ImageTransformType[] = [
  'original',
  'bannerCrop',
  'fullFit',
  'landscapeLgCrop',
  'landscapeMdCrop',
  'landscapeSmCrop',
  'squareMdFit',
  'squareSmFit',
  'squareLgCrop',
  'squareMdCrop',
  'squareSmCrop',
];

export interface ImageEntry {
  noCrop?: boolean;
  mimeType: string;
  src: string;
  alt?: string;
  title?: string;
  caption?: string;
  height?: number;
  width?: number;
  srcSet?: string;
  transforms?: ImageTransforms;
}

export type ImageFragmentWithTransform = ImageFragment & ImageSize_AllFragment;

export const parseImage = (data: MaybeArrayOf<ImageFragmentWithTransform>): ImageEntry | null => {
  const image = firstNonNullable(data);

  if (!image || !image.url) return null;

  const { alt, title, url: src, height, width, mimeType } = pickFromUnion(image, []);

  const imageEntry: ImageEntry = {
    mimeType: toString(mimeType),
    src,
    height,
    width,
    alt,
    title,
    // caption,
    transforms: imageTransformKeys.reduce((result, transformType) => {
      const url = image[`${transformType}_url`];
      const width = toNumber(image[`${transformType}_width`]);
      const height = toNumber(image[`${transformType}_height`]);

      if (url)
        result[transformType] = {
          url,
          width,
          height,
        };

      return result;
    }, {} as ImageTransforms),
  };

  return deleteUndefined(imageEntry);
};

export const parseImageMany = (maybeImage: MaybeArrayOf<ImageFragmentWithTransform>) => {
  const imageArr = makeNonNullableArray(maybeImage);
  return makeNonNullableArray(imageArr.map(parseImage));
};

export const isSvg = (image?: Maybe<ImageEntry>) => {
  return image?.mimeType === 'image/svg+xml';
};

export type StyledImageStyle = 'default' | 'disableAnimation' | 'disableStyle';

export const parseStyledImageStyle = (data?: any): StyledImageStyle => {
  switch (data) {
    case 'disableAnimation':
    case 'disableStyle':
      return data;
    default:
      return 'default';
  }
};
